/**
 * CLIKKER Admin Control Center
 * Asset: clikker-admin.js
 * Version: 1.1.0 — Firebase 2.4 aligned admin release
 * Matches page.clikker-admin.liquid
 *
 * IMPORTANT
 * --------------------------------------------------------------------------
 * - Firestore rules must enforce admin/super_admin access.
 * - Financial balances are read-only in this client.
 * - Real payout execution must happen through secure backend functions.
 */
(function () {
  "use strict";

  var VERSION = "1.1.0";
  var ALLOWED_ROLES = ["admin", "super_admin"];
  var MAX_COLLECTION_DOCS = 2500;

  var appRoot = document.getElementById("clikkerAdminApp");

  var ROUTES = {
    admin:
      window.CLIKKER_ADMIN_ROUTE ||
      (appRoot && appRoot.dataset.adminRoute) ||
      "/pages/clikker-admin",
    login:
      window.CLIKKER_LOGIN_ROUTE ||
      (appRoot && appRoot.dataset.loginRoute) ||
      "/pages/clikker-login?mode=login",
    creator:
      window.CLIKKER_CREATOR_ROUTE ||
      (appRoot && appRoot.dataset.creatorRoute) ||
      "/pages/clikker-profile",
    publicStore:
      window.CLIKKER_PUBLIC_STORE_ROUTE ||
      (appRoot && appRoot.dataset.publicStoreRoute) ||
      "/pages/c?creator="
  };

  var state = {
    adminUser: null,
    adminProfile: null,
    currentView: "dashboard",
    loading: false,
    creators: [],
    users: [],
    stores: [],
    products: [],
    orders: [],
    payouts: [],
    ledger: [],
    documents: [],
    auditLogs: [],
    creatorRows: [],
    creatorFilters: {
      search: "",
      account: "all",
      onboarding: "all",
      store: "all",
      payout: "all",
      category: "all",
      sort: "joined_desc",
      page: 1,
      pageSize: 50
    },
    generic: {
      view: "",
      search: "",
      status: "all",
      page: 1,
      pageSize: 50,
      rows: []
    },
    selectedCreatorId: "",
    selectedCreatorBundle: null,
    selectedCreatorTab: "overview",
    pendingConfirm: null
  };

  var el = {};

  function byId(id) {
    return document.getElementById(id);
  }

  function cache() {
    [
      "clikkerAdminApp",
      "clikkerAdminLoading",
      "clikkerAdminAccessDenied",
      "clikkerAdminAccessDeniedTitle",
      "clikkerAdminAccessDeniedMessage",
      "clikkerAdminDeniedLogout",
      "clikkerAdminError",
      "clikkerAdminErrorTitle",
      "clikkerAdminErrorMessage",
      "clikkerAdminRetry",
      "clikkerAdminShell",
      "clikkerAdminSidebar",
      "clikkerAdminNavigation",
      "clikkerAdminCreatorsNavCount",
      "clikkerAdminOrdersNavCount",
      "clikkerAdminPayoutsNavCount",
      "clikkerAdminUserAvatar",
      "clikkerAdminUserName",
      "clikkerAdminUserEmail",
      "clikkerAdminUserRole",
      "clikkerAdminLogout",
      "clikkerAdminMobileMenu",
      "clikkerAdminPageTitle",
      "clikkerAdminPageSubtitle",
      "clikkerAdminTopbarActions",
      "clikkerAdminAlert",
      "clikkerAdminView",
      "clikkerAdminDashboardTemplate",
      "clikkerAdminCreatorsTemplate",
      "clikkerAdminGenericListTemplate",
      "clikkerAdminSettingsTemplate",
      "clikkerAdminCreatorDrawer",
      "clikkerAdminCreatorDrawerClose",
      "clikkerAdminCreatorDrawerTabs",
      "clikkerAdminCreatorDrawerAlert",
      "clikkerAdminCreatorDrawerLoading",
      "clikkerAdminCreatorDrawerAvatar",
      "clikkerAdminCreatorDrawerTitle",
      "clikkerAdminCreatorDrawerAccountStatus",
      "clikkerAdminCreatorDrawerMeta",
      "clikkerAdminCreatorDrawerCreatorId",
      "clikkerAdminCreatorDrawerStoreId",
      "clikkerAdminCreatorDrawerSaveState",
      "clikkerAdminCreatorOpenProfile",
      "clikkerAdminCreatorCloseFooter",
      "clikkerAdminConfirmDialog",
      "clikkerAdminConfirmIcon",
      "clikkerAdminConfirmTitle",
      "clikkerAdminConfirmMessage",
      "clikkerAdminConfirmReasonWrap",
      "clikkerAdminConfirmReason",
      "clikkerAdminConfirmAlert",
      "clikkerAdminConfirmCancel",
      "clikkerAdminConfirmSubmit"
    ].forEach(function (id) {
      el[id] = byId(id);
    });
  }

  function clean(value) {
    return String(value == null ? "" : value).trim();
  }

  function lower(value) {
    return clean(value).toLowerCase();
  }

  function escapeHTML(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (char) {
      return {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;"
      }[char];
    });
  }

  function clone(value) {
    return JSON.parse(JSON.stringify(value == null ? null : value));
  }

  function number(value) {
    var parsed = Number(value || 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function bool(value) {
    return value === true || value === "true" || value === 1 || value === "1";
  }

  function titleCase(value) {
    return clean(value)
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, function (letter) {
        return letter.toUpperCase();
      });
  }

  function db() {
    if (window.Clikker && window.Clikker.db) return window.Clikker.db;
    if (window.firebase && typeof window.firebase.firestore === "function") {
      return window.firebase.firestore();
    }
    throw new Error("Firestore is not available.");
  }

  function auth() {
    if (window.Clikker && window.Clikker.auth) return window.Clikker.auth;
    if (window.firebase && typeof window.firebase.auth === "function") {
      return window.firebase.auth();
    }
    throw new Error("Firebase Auth is not available.");
  }

  function serverTimestamp() {
    if (window.Clikker && typeof window.Clikker.serverTimestamp === "function") {
      return window.Clikker.serverTimestamp();
    }
    if (window.firebase && window.firebase.firestore && window.firebase.firestore.FieldValue) {
      return window.firebase.firestore.FieldValue.serverTimestamp();
    }
    return new Date();
  }

  function currentUid() {
    return clean(state.adminUser && state.adminUser.uid);
  }

  function money(value, currency) {
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency || "INR",
        maximumFractionDigits: 0
      }).format(number(value));
    } catch (_) {
      return "₹" + number(value).toLocaleString("en-IN");
    }
  }

  function dateValue(value) {
    if (!value) return null;
    if (value.toDate && typeof value.toDate === "function") return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    if (value instanceof Date) return value;
    var parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  function formatDate(value, includeTime) {
    var date = dateValue(value);
    if (!date) return "—";
    try {
      return new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: includeTime ? "2-digit" : undefined,
        minute: includeTime ? "2-digit" : undefined
      }).format(date);
    } catch (_) {
      return date.toLocaleString();
    }
  }

  function timestampMs(value) {
    var date = dateValue(value);
    return date ? date.getTime() : 0;
  }

  function firstValue() {
    for (var i = 0; i < arguments.length; i += 1) {
      var value = arguments[i];
      if (value !== undefined && value !== null && clean(value) !== "") return value;
    }
    return "";
  }

  function showNode(node) {
    if (node) node.hidden = false;
  }

  function hideNode(node) {
    if (node) node.hidden = true;
  }

  function setText(id, value) {
    var node = byId(id);
    if (node) node.textContent = value == null || value === "" ? "—" : String(value);
  }

  function setValue(id, value) {
    var node = byId(id);
    if (node) node.value = value == null ? "" : String(value);
  }

  function setAppState(value) {
    if (el.clikkerAdminApp) el.clikkerAdminApp.dataset.state = value;
  }

  function showLoading() {
    setAppState("loading");
    showNode(el.clikkerAdminLoading);
    hideNode(el.clikkerAdminAccessDenied);
    hideNode(el.clikkerAdminError);
    hideNode(el.clikkerAdminShell);
  }

  function showShell() {
    setAppState("ready");
    hideNode(el.clikkerAdminLoading);
    hideNode(el.clikkerAdminAccessDenied);
    hideNode(el.clikkerAdminError);
    showNode(el.clikkerAdminShell);
  }

  function showAccessDenied(message) {
    setAppState("denied");
    hideNode(el.clikkerAdminLoading);
    hideNode(el.clikkerAdminError);
    hideNode(el.clikkerAdminShell);
    showNode(el.clikkerAdminAccessDenied);
    if (el.clikkerAdminAccessDeniedMessage) {
      el.clikkerAdminAccessDeniedMessage.textContent =
        message || "Your account does not have permission to open this page.";
    }
  }

  function showError(title, message) {
    setAppState("error");
    hideNode(el.clikkerAdminLoading);
    hideNode(el.clikkerAdminAccessDenied);
    hideNode(el.clikkerAdminShell);
    showNode(el.clikkerAdminError);
    if (el.clikkerAdminErrorTitle) el.clikkerAdminErrorTitle.textContent = title;
    if (el.clikkerAdminErrorMessage) el.clikkerAdminErrorMessage.textContent = message;
  }

  function showAlert(message, type) {
    if (!el.clikkerAdminAlert) return;
    el.clikkerAdminAlert.className = "cad-alert is-visible cad-alert--" + (type || "info");
    el.clikkerAdminAlert.textContent = message;
  }

  function clearAlert() {
    if (!el.clikkerAdminAlert) return;
    el.clikkerAdminAlert.className = "cad-alert";
    el.clikkerAdminAlert.textContent = "";
  }

  function localAlert(id, message, type) {
    var node = byId(id);
    if (!node) return;
    node.className = "cad-alert is-visible cad-alert--" + (type || "info");
    node.textContent = message;
  }

  function clearLocalAlert(id) {
    var node = byId(id);
    if (!node) return;
    node.className = "cad-alert";
    node.textContent = "";
  }

  function firebaseMessage(error, fallback) {
    var code = clean(error && error.code);
    if (code === "permission-denied" || code === "firestore/permission-denied") {
      return "Permission denied. Confirm the current user has an active admin role and update Firestore rules.";
    }
    if (code === "unavailable" || code === "firestore/unavailable") {
      return "Firebase is temporarily unavailable. Try again shortly.";
    }
    return clean(error && error.message) || fallback || "Something went wrong.";
  }

  async function waitForFirebase() {
    if (window.Clikker && typeof window.Clikker.waitUntilReady === "function") {
      try {
        await window.Clikker.waitUntilReady();
      } catch (_) {}
    }

    var attempts = 0;
    while (attempts < 160) {
      try {
        db();
        auth();
        return;
      } catch (_) {}
      await new Promise(function (resolve) { setTimeout(resolve, 50); });
      attempts += 1;
    }
    throw new Error("Firebase did not load.");
  }

  function authUserOnce() {
    if (window.Clikker && window.Clikker.currentFirebaseUser) {
      return Promise.resolve(window.Clikker.currentFirebaseUser);
    }
    return new Promise(function (resolve, reject) {
      var unsubscribe = auth().onAuthStateChanged(
        function (user) {
          unsubscribe();
          resolve(user || null);
        },
        function (error) {
          unsubscribe();
          reject(error);
        }
      );
    });
  }

  async function loadAdminProfile(user) {
    var snap = await db().collection("users").doc(user.uid).get();
    if (!snap.exists) return null;
    return Object.assign({ id: snap.id }, snap.data());
  }

  function loginRedirectURL() {
    var base = ROUTES.login || "/pages/clikker-login?mode=login";
    var separator = base.indexOf("?") >= 0 ? "&" : "?";
    return base + separator + "next=" + encodeURIComponent(ROUTES.admin);
  }

  async function boot() {
    showLoading();
    try {
      await waitForFirebase();
      var user = await authUserOnce();
      if (!user) {
        location.href = loginRedirectURL();
        return;
      }

      state.adminUser = user;
      state.adminProfile = await loadAdminProfile(user);

      var role = lower(state.adminProfile && state.adminProfile.role);
      var status = lower(state.adminProfile && state.adminProfile.status) || "active";

      if (ALLOWED_ROLES.indexOf(role) < 0 || status !== "active") {
        renderAdminUser();
        showAccessDenied(
          role
            ? "This account is not an active Clikker administrator."
            : "No admin role is assigned to this account."
        );
        return;
      }

      renderAdminUser();
      await refreshAllData(true);
      showShell();
      renderView(new URL(location.href).searchParams.get("view") || "dashboard");
      console.info("[Clikker Admin] Ready", VERSION);
    } catch (error) {
      console.error("[Clikker Admin] Boot failed", error);
      showError("Unable to open admin workspace", firebaseMessage(error));
    }
  }

  function renderAdminUser() {
    var user = state.adminUser || {};
    var profile = state.adminProfile || {};
    var name = clean(firstValue(profile.name, profile.displayName, user.displayName, "Administrator"));
    var email = clean(firstValue(profile.email, user.email));
    var role = titleCase(firstValue(profile.role, "admin"));
    var photo = clean(firstValue(profile.photoURL, profile.profileImage, user.photoURL));

    if (el.clikkerAdminUserName) el.clikkerAdminUserName.textContent = name;
    if (el.clikkerAdminUserEmail) el.clikkerAdminUserEmail.textContent = email;
    if (el.clikkerAdminUserRole) el.clikkerAdminUserRole.textContent = role;
    if (el.clikkerAdminUserAvatar) {
      el.clikkerAdminUserAvatar.innerHTML = photo
        ? '<img src="' + escapeHTML(photo) + '" alt="' + escapeHTML(name) + '">'
        : escapeHTML(name.charAt(0).toUpperCase());
    }
  }

  async function getCollection(name) {
    var snap = await db().collection(name).limit(MAX_COLLECTION_DOCS).get();
    return snap.docs.map(function (doc) {
      return Object.assign({ id: doc.id }, doc.data());
    });
  }

  async function getCollectionFallback(names) {
    var lastError = null;
    for (var i = 0; i < names.length; i += 1) {
      try {
        var rows = await getCollection(names[i]);
        return { name: names[i], rows: rows };
      } catch (error) {
        lastError = error;
        if (clean(error && error.code) === "permission-denied") throw error;
      }
    }
    if (lastError) console.warn("[Clikker Admin] Collection fallback failed", names, lastError);
    return { name: names[0], rows: [] };
  }

  async function refreshAllData(silent) {
    if (state.loading) return;
    state.loading = true;
    if (!silent) showAlert("Refreshing platform data…", "info");

    try {
      var results = await Promise.all([
        getCollection("users"),
        getCollection("creators"),
        getCollection("stores"),
        getCollection("products"),
        getCollectionFallback(["orders", "creatorOrders"]),
        getCollectionFallback(["creatorPayouts", "payouts"]),
        getCollectionFallback(["creatorLedger", "ledger"]),
        getCollectionFallback(["creatorDocuments", "documents"]),
        getCollectionFallback(["adminAuditLogs", "activityLogs"])
      ]);

      state.users = results[0];
      state.creators = results[1];
      state.stores = results[2];
      state.products = results[3];
      state.orders = results[4].rows;
      state.payouts = results[5].rows;
      state.ledger = results[6].rows;
      state.documents = results[7].rows;
      state.auditLogs = results[8].rows;
      state.creatorRows = buildCreatorRows();
      updateNavigationCounts();

      if (!silent) showAlert("Platform data refreshed.", "success");
    } catch (error) {
      console.error("[Clikker Admin] Refresh failed", error);
      if (!silent) showAlert(firebaseMessage(error, "Platform data could not be refreshed."), "error");
      else throw error;
    } finally {
      state.loading = false;
    }
  }

  function creatorIdOf(row) {
    return clean(firstValue(row.creatorId, row.creator_id, row.id));
  }

  function ownerIdOf(row) {
    return clean(firstValue(row.ownerId, row.owner_id, row.userId, row.user_id, row.uid));
  }

  function storeIdOf(row) {
    return clean(firstValue(row.storeId, row.store_id));
  }

  function findUserForCreator(creator) {
    var ownerId = ownerIdOf(creator);
    return state.users.find(function (user) {
      return user.id === ownerId || clean(user.uid) === ownerId || clean(user.creatorId) === creator.id;
    }) || null;
  }

  function findStoreForCreator(creator) {
    var explicitStoreId = storeIdOf(creator);
    return state.stores.find(function (store) {
      return (
        store.id === explicitStoreId ||
        creatorIdOf(store) === creator.id ||
        ownerIdOf(store) === ownerIdOf(creator)
      );
    }) || null;
  }

  function belongsToCreator(row, creator, store) {
    var creatorId = creator.id;
    var ownerId = ownerIdOf(creator);
    var storeId = store && store.id;
    return (
      creatorIdOf(row) === creatorId ||
      ownerIdOf(row) === ownerId ||
      (storeId && storeIdOf(row) === storeId)
    );
  }

  function orderPaidAmount(order) {
    return number(firstValue(order.paidAmount, order.paid_amount, order.totalAmount, order.total_amount, order.amount, order.total));
  }

  function ledgerGross(entry) {
    return number(firstValue(entry.grossAmount, entry.gross_amount, entry.amount));
  }

  function ledgerFees(entry) {
    return number(firstValue(entry.feeAmount, entry.fee_amount, entry.platformFee, entry.platform_fee, entry.fees));
  }

  function ledgerNet(entry) {
    var direct = firstValue(entry.netAmount, entry.net_amount, entry.creatorNet, entry.creator_net);
    return clean(direct) !== "" ? number(direct) : ledgerGross(entry) - ledgerFees(entry);
  }

  function payoutAmount(payout) {
    return number(firstValue(payout.amount, payout.payoutAmount, payout.payout_amount));
  }

  function accountStatusOf(creator, user) {
    return lower(firstValue(creator.status, user && user.status, "active"));
  }

  function onboardingCompleteOf(creator, store) {
    return bool(
      firstValue(
        creator.onboardingCompleted,
        creator.onboarding_completed,
        store && store.onboardingCompleted,
        store && store.onboarding_completed
      )
    );
  }

  function payoutProfileOf(creator) {
    return creator.payoutProfile || creator.payout_profile || {};
  }

  function payoutSummaryOf(creator) {
    return creator.payoutSummary || creator.payout_summary || {};
  }

  function buildCreatorRows() {
    return state.creators.map(function (creator) {
      var user = findUserForCreator(creator);
      var store = findStoreForCreator(creator);
      var products = state.products.filter(function (product) {
        return belongsToCreator(product, creator, store);
      });
      var orders = state.orders.filter(function (order) {
        return belongsToCreator(order, creator, store);
      });
      var ledger = state.ledger.filter(function (entry) {
        return belongsToCreator(entry, creator, store);
      });
      var payouts = state.payouts.filter(function (payout) {
        return belongsToCreator(payout, creator, store);
      });
      var payoutProfile = payoutProfileOf(creator);
      var payoutSummary = payoutSummaryOf(creator);
      var paidOrders = orders.filter(function (order) {
        return ["paid", "new", "in_progress", "delivered", "completed"].indexOf(lower(order.status)) >= 0 || bool(order.isPaid);
      });
      var gross = ledger.length
        ? ledger.reduce(function (sum, entry) { return sum + ledgerGross(entry); }, 0)
        : paidOrders.reduce(function (sum, order) { return sum + orderPaidAmount(order); }, 0);
      var net = ledger.reduce(function (sum, entry) { return sum + ledgerNet(entry); }, 0);
      var totalPaid = payouts
        .filter(function (payout) { return ["paid", "completed", "success"].indexOf(lower(payout.status)) >= 0; })
        .reduce(function (sum, payout) { return sum + payoutAmount(payout); }, 0);

      return {
        id: creator.id,
        creator: creator,
        user: user,
        store: store,
        products: products,
        orders: orders,
        ledger: ledger,
        payouts: payouts,
        documents: state.documents.filter(function (doc) { return belongsToCreator(doc, creator, store); }),
        auditLogs: state.auditLogs.filter(function (log) { return belongsToCreator(log, creator, store); }),
        name: clean(firstValue(creator.displayName, creator.name, user && user.name, user && user.displayName, "Creator")),
        email: clean(firstValue(creator.email, creator.supportEmail, user && user.email)),
        phone: clean(firstValue(creator.phone, creator.phoneNumber, user && user.phone)),
        handle: clean(firstValue(creator.handle, creator.username, store && store.slug, store && store.handle)),
        category: clean(firstValue(creator.category, store && store.category, "Other")),
        accountStatus: accountStatusOf(creator, user),
        onboardingCompleted: onboardingCompleteOf(creator, store),
        storeStatus: lower(firstValue(store && store.status, "missing")),
        storeVisibility: lower(firstValue(store && store.visibility, "private")),
        payoutStatus: lower(firstValue(payoutProfile.status, payoutProfile.providerStatus, "not_connected")),
        joinedAt: firstValue(creator.createdAt, creator.created_at, user && user.createdAt, user && user.created_at),
        lastActiveAt: firstValue(creator.lastActiveAt, creator.last_active_at, user && user.lastLoginAt, user && user.last_login_at),
        grossSales: gross,
        netEarnings: net,
        availableBalance: number(firstValue(payoutSummary.availableBalance, payoutSummary.available_balance)),
        pendingBalance: number(firstValue(payoutSummary.pendingBalance, payoutSummary.pending_balance)),
        totalPaid: number(firstValue(payoutSummary.totalPaid, payoutSummary.total_paid, totalPaid))
      };
    });
  }

  function updateNavigationCounts() {
    var pendingOrders = state.orders.filter(function (order) {
      return ["new", "paid", "in_progress"].indexOf(lower(order.status)) >= 0;
    }).length;
    var pendingPayouts = state.payouts.filter(function (payout) {
      return ["pending", "processing", "failed"].indexOf(lower(payout.status)) >= 0;
    }).length;

    setCounter(el.clikkerAdminCreatorsNavCount, state.creatorRows.length);
    setCounter(el.clikkerAdminOrdersNavCount, pendingOrders);
    setCounter(el.clikkerAdminPayoutsNavCount, pendingPayouts);
  }

  function setCounter(node, value) {
    if (!node) return;
    node.textContent = String(value || 0);
    node.hidden = !(value > 0);
  }

  function normalizeView(view) {
    return [
      "dashboard",
      "creators",
      "stores",
      "products",
      "orders",
      "payouts",
      "transactions",
      "support",
      "settings"
    ].indexOf(view) >= 0
      ? view
      : "dashboard";
  }

  function syncViewURL(view) {
    try {
      var url = new URL(location.href);
      if (view === "dashboard") url.searchParams.delete("view");
      else url.searchParams.set("view", view);
      history.replaceState({ view: view }, "", url);
    } catch (_) {}
  }

  function setHeader(title, subtitle, actionsHTML) {
    if (el.clikkerAdminPageTitle) el.clikkerAdminPageTitle.textContent = title;
    if (el.clikkerAdminPageSubtitle) el.clikkerAdminPageSubtitle.textContent = subtitle;
    if (el.clikkerAdminTopbarActions) el.clikkerAdminTopbarActions.innerHTML = actionsHTML || "";
  }

  function renderView(view) {
    view = normalizeView(view);
    state.currentView = view;
    syncViewURL(view);
    clearAlert();

    if (el.clikkerAdminNavigation) {
      el.clikkerAdminNavigation.querySelectorAll("[data-admin-view]").forEach(function (button) {
        var active = button.dataset.adminView === view;
        button.classList.toggle("is-active", active);
        if (active) button.setAttribute("aria-current", "page");
        else button.removeAttribute("aria-current");
      });
    }

    if (view === "dashboard") renderDashboard();
    else if (view === "creators") renderCreators();
    else if (view === "settings") renderSettings();
    else if (view === "support") renderSupport();
    else renderGeneric(view);

    if (el.clikkerAdminSidebar) el.clikkerAdminSidebar.classList.remove("is-open");
    window.scrollTo({ top: 0, behavior: "auto" });
  }

  function cloneTemplate(template) {
    if (!template) throw new Error("Required admin template is missing.");
    el.clikkerAdminView.innerHTML = "";
    el.clikkerAdminView.appendChild(template.content.cloneNode(true));
  }

  function bindGoViewButtons() {
    el.clikkerAdminView.querySelectorAll("[data-admin-go-view]").forEach(function (button) {
      button.addEventListener("click", function () {
        renderView(button.dataset.adminGoView);
      });
    });
  }

  function renderDashboard() {
    cloneTemplate(el.clikkerAdminDashboardTemplate);
    setHeader("Dashboard", "Monitor creators, commerce and platform operations", "");

    var creators = state.creatorRows;
    var publishedStores = creators.filter(function (row) { return row.storeStatus === "published"; }).length;
    var paidOrders = state.orders.filter(function (order) {
      return ["paid", "new", "in_progress", "delivered", "completed"].indexOf(lower(order.status)) >= 0 || bool(order.isPaid);
    });
    var deliveredOrders = paidOrders.filter(function (order) {
      return ["delivered", "completed"].indexOf(lower(order.status)) >= 0;
    }).length;
    var grossSales = paidOrders.reduce(function (sum, order) { return sum + orderPaidAmount(order); }, 0);
    var creatorEarnings = state.ledger.reduce(function (sum, entry) { return sum + ledgerNet(entry); }, 0);
    var platformRevenue = state.ledger.reduce(function (sum, entry) { return sum + ledgerFees(entry); }, 0);
    var pendingPayouts = state.payouts
      .filter(function (payout) { return ["pending", "processing", "scheduled"].indexOf(lower(payout.status)) >= 0; })
      .reduce(function (sum, payout) { return sum + payoutAmount(payout); }, 0);
    var failedPayouts = state.payouts.filter(function (payout) {
      return ["failed", "rejected"].indexOf(lower(payout.status)) >= 0;
    });
    var monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    var joinedThisMonth = creators.filter(function (row) { return timestampMs(row.joinedAt) >= monthStart.getTime(); }).length;

    setText("clikkerAdminKpiCreators", creators.length);
    setText("clikkerAdminKpiCreatorsNote", joinedThisMonth + " joined this month");
    setText("clikkerAdminKpiStores", publishedStores);
    setText(
      "clikkerAdminKpiStoresNote",
      creators.length ? Math.round((publishedStores / creators.length) * 100) + "% publication rate" : "0% publication rate"
    );
    setText("clikkerAdminKpiOrders", paidOrders.length);
    setText("clikkerAdminKpiOrdersNote", deliveredOrders + " delivered");
    setText("clikkerAdminKpiGrossSales", money(grossSales));
    setText("clikkerAdminKpiCreatorEarnings", money(creatorEarnings));
    setText("clikkerAdminKpiRevenue", money(platformRevenue));
    setText("clikkerAdminKpiPendingPayouts", money(pendingPayouts));
    setText(
      "clikkerAdminKpiPendingPayoutsNote",
      uniqueCreatorsFromRows(state.payouts.filter(function (payout) {
        return ["pending", "processing", "scheduled"].indexOf(lower(payout.status)) >= 0;
      })).length + " creators affected"
    );
    setText("clikkerAdminKpiFailedPayouts", failedPayouts.length);

    setText("clikkerAdminFunnelRegistered", state.users.filter(function (user) { return lower(user.role) === "creator"; }).length || creators.length);
    setText("clikkerAdminFunnelProfile", creators.length);
    setText("clikkerAdminFunnelPublished", publishedStores);
    setText("clikkerAdminFunnelProduct", creators.filter(function (row) { return row.products.length > 0; }).length);
    setText("clikkerAdminFunnelOrder", creators.filter(function (row) { return row.orders.length > 0; }).length);

    renderAttentionList();
    renderRecentCreators();

    var refreshButton = byId("clikkerAdminDashboardRefresh");
    if (refreshButton) {
      refreshButton.addEventListener("click", async function () {
        refreshButton.disabled = true;
        refreshButton.textContent = "Refreshing…";
        await refreshAllData(false);
        renderDashboard();
      });
    }

    bindGoViewButtons();
  }

  function uniqueCreatorsFromRows(rows) {
    var ids = {};
    rows.forEach(function (row) {
      var id = creatorIdOf(row) || ownerIdOf(row);
      if (id) ids[id] = true;
    });
    return Object.keys(ids);
  }

  function renderAttentionList() {
    var list = byId("clikkerAdminAttentionList");
    var empty = byId("clikkerAdminAttentionEmpty");
    if (!list || !empty) return;

    var items = [];
    state.creatorRows.forEach(function (row) {
      if (!row.onboardingCompleted) {
        items.push({ type: "warning", title: row.name, note: "Onboarding incomplete", creatorId: row.id });
      }
      if (row.payoutStatus === "pending_verification") {
        items.push({ type: "warning", title: row.name, note: "Payout verification pending", creatorId: row.id });
      }
      if (["suspended", "disabled"].indexOf(row.accountStatus) >= 0) {
        items.push({ type: "danger", title: row.name, note: "Creator account " + row.accountStatus, creatorId: row.id });
      }
    });
    state.payouts.forEach(function (payout) {
      if (["failed", "rejected"].indexOf(lower(payout.status)) >= 0) {
        items.push({
          type: "danger",
          title: clean(firstValue(payout.reference, payout.id, "Payout")),
          note: "Failed payout " + money(payoutAmount(payout), payout.currency || "INR"),
          creatorId: creatorIdOf(payout)
        });
      }
    });

    items = items.slice(0, 10);
    list.innerHTML = items.map(function (item) {
      return (
        '<button class="cad-attention-item cad-attention-item--' + escapeHTML(item.type) + '" type="button" data-attention-creator="' +
        escapeHTML(item.creatorId) + '">' +
        '<span class="cad-attention-item__mark">' + (item.type === "danger" ? "!" : "•") + "</span>" +
        "<span><strong>" + escapeHTML(item.title) + "</strong><small>" + escapeHTML(item.note) + "</small></span>" +
        "</button>"
      );
    }).join("");

    empty.hidden = items.length > 0;
    list.hidden = items.length === 0;

    list.querySelectorAll("[data-attention-creator]").forEach(function (button) {
      button.addEventListener("click", function () {
        var creatorId = button.dataset.attentionCreator;
        if (creatorId) openCreatorDrawer(creatorId);
      });
    });
  }

  function creatorCell(row) {
    var initial = (row.name || "C").charAt(0).toUpperCase();
    var photo = clean(firstValue(row.creator.profileImageURL, row.creator.profileImage, row.user && row.user.photoURL, row.store && row.store.profileImageURL));
    return (
      '<div class="cad-table-creator">' +
      '<span class="cad-table-creator__avatar">' +
      (photo ? '<img src="' + escapeHTML(photo) + '" alt="">' : escapeHTML(initial)) +
      "</span>" +
      "<span><strong>" + escapeHTML(row.name) + "</strong><small>" + escapeHTML(row.email || "No email") + "</small></span>" +
      "</div>"
    );
  }

  function statusHTML(value) {
    var key = lower(value) || "unknown";
    var statusClass = "cad-status";
    if (["active", "published", "public", "verified", "paid", "completed", "success", "delivered"].indexOf(key) >= 0) {
      statusClass += " cad-status--success";
    } else if (["pending", "pending_verification", "processing", "draft", "paused", "in_progress", "new"].indexOf(key) >= 0) {
      statusClass += " cad-status--warning";
    } else if (["suspended", "disabled", "failed", "rejected", "cancelled"].indexOf(key) >= 0) {
      statusClass += " cad-status--danger";
    }
    return '<span class="' + statusClass + '">' + escapeHTML(titleCase(key)) + "</span>";
  }

  function renderRecentCreators() {
    var body = byId("clikkerAdminRecentCreatorsBody");
    if (!body) return;
    var rows = state.creatorRows
      .slice()
      .sort(function (a, b) { return timestampMs(b.joinedAt) - timestampMs(a.joinedAt); })
      .slice(0, 8);

    body.innerHTML = rows.length
      ? rows.map(function (row) {
          return (
            "<tr>" +
            "<td>" + creatorCell(row) + "</td>" +
            "<td>" + escapeHTML(row.handle ? "@" + row.handle : "—") + "</td>" +
            "<td>" + escapeHTML(row.category || "—") + "</td>" +
            "<td>" + statusHTML(row.storeStatus) + "</td>" +
            "<td>" + statusHTML(row.payoutStatus) + "</td>" +
            "<td>" + escapeHTML(formatDate(row.joinedAt)) + "</td>" +
            '<td><button class="cad-btn cad-btn--secondary cad-btn--small" type="button" data-view-creator="' + escapeHTML(row.id) + '">View</button></td>' +
            "</tr>"
          );
        }).join("")
      : '<tr><td colspan="7" class="cad-table__empty">No creator records found.</td></tr>';

    bindCreatorOpenButtons(body);
  }

  function renderCreators() {
    cloneTemplate(el.clikkerAdminCreatorsTemplate);
    setHeader("Creators", "Search and inspect every creator account", "");
    populateCreatorCategoryFilter();
    hydrateCreatorFilterControls();
    bindCreatorFilters();
    renderCreatorTable();
  }

  function populateCreatorCategoryFilter() {
    var select = byId("clikkerAdminCreatorCategoryFilter");
    if (!select) return;
    var categories = {};
    state.creatorRows.forEach(function (row) {
      if (row.category) categories[row.category] = true;
    });
    Object.keys(categories)
      .sort()
      .forEach(function (category) {
        var option = document.createElement("option");
        option.value = category;
        option.textContent = category;
        select.appendChild(option);
      });
  }

  function hydrateCreatorFilterControls() {
    setValue("clikkerAdminCreatorsSearch", state.creatorFilters.search);
    setValue("clikkerAdminCreatorAccountFilter", state.creatorFilters.account);
    setValue("clikkerAdminCreatorOnboardingFilter", state.creatorFilters.onboarding);
    setValue("clikkerAdminCreatorStoreFilter", state.creatorFilters.store);
    setValue("clikkerAdminCreatorPayoutFilter", state.creatorFilters.payout);
    setValue("clikkerAdminCreatorCategoryFilter", state.creatorFilters.category);
    setValue("clikkerAdminCreatorSort", state.creatorFilters.sort);
    setValue("clikkerAdminCreatorsPageSize", state.creatorFilters.pageSize);
  }

  function bindCreatorFilters() {
    var search = byId("clikkerAdminCreatorsSearch");
    var fields = [
      ["clikkerAdminCreatorAccountFilter", "account"],
      ["clikkerAdminCreatorOnboardingFilter", "onboarding"],
      ["clikkerAdminCreatorStoreFilter", "store"],
      ["clikkerAdminCreatorPayoutFilter", "payout"],
      ["clikkerAdminCreatorCategoryFilter", "category"],
      ["clikkerAdminCreatorSort", "sort"]
    ];

    if (search) {
      search.addEventListener("input", function () {
        state.creatorFilters.search = clean(search.value);
        state.creatorFilters.page = 1;
        renderCreatorTable();
      });
    }

    fields.forEach(function (pair) {
      var node = byId(pair[0]);
      if (!node) return;
      node.addEventListener("change", function () {
        state.creatorFilters[pair[1]] = node.value;
        state.creatorFilters.page = 1;
        renderCreatorTable();
      });
    });

    var pageSize = byId("clikkerAdminCreatorsPageSize");
    if (pageSize) {
      pageSize.addEventListener("change", function () {
        state.creatorFilters.pageSize = number(pageSize.value) || 50;
        state.creatorFilters.page = 1;
        renderCreatorTable();
      });
    }

    var clearButton = byId("clikkerAdminCreatorsClearFilters");
    if (clearButton) {
      clearButton.addEventListener("click", function () {
        state.creatorFilters = {
          search: "",
          account: "all",
          onboarding: "all",
          store: "all",
          payout: "all",
          category: "all",
          sort: "joined_desc",
          page: 1,
          pageSize: state.creatorFilters.pageSize || 50
        };
        hydrateCreatorFilterControls();
        renderCreatorTable();
      });
    }

    var refreshButton = byId("clikkerAdminCreatorsRefresh");
    if (refreshButton) {
      refreshButton.addEventListener("click", async function () {
        refreshButton.disabled = true;
        refreshButton.textContent = "Refreshing…";
        await refreshAllData(false);
        renderCreators();
      });
    }

    var exportButton = byId("clikkerAdminCreatorsExport");
    if (exportButton) {
      exportButton.addEventListener("click", function () {
        exportCreatorsCSV(filteredCreatorRows());
      });
    }
  }

  function filteredCreatorRows() {
    var filters = state.creatorFilters;
    var search = lower(filters.search);
    var rows = state.creatorRows.filter(function (row) {
      if (search) {
        var haystack = lower([
          row.name,
          row.email,
          row.handle,
          row.id,
          row.store && row.store.id,
          ownerIdOf(row.creator)
        ].join(" "));
        if (haystack.indexOf(search) < 0) return false;
      }
      if (filters.account !== "all" && row.accountStatus !== filters.account) return false;
      if (filters.onboarding === "completed" && !row.onboardingCompleted) return false;
      if (filters.onboarding === "incomplete" && row.onboardingCompleted) return false;
      if (filters.store !== "all" && row.storeStatus !== filters.store) return false;
      if (filters.payout !== "all" && row.payoutStatus !== filters.payout) return false;
      if (filters.category !== "all" && row.category !== filters.category) return false;
      return true;
    });

    rows.sort(function (a, b) {
      if (filters.sort === "joined_asc") return timestampMs(a.joinedAt) - timestampMs(b.joinedAt);
      if (filters.sort === "orders_desc") return b.orders.length - a.orders.length;
      if (filters.sort === "earnings_desc") return b.netEarnings - a.netEarnings;
      if (filters.sort === "name_asc") return a.name.localeCompare(b.name);
      return timestampMs(b.joinedAt) - timestampMs(a.joinedAt);
    });

    return rows;
  }

  function renderCreatorTable() {
    var body = byId("clikkerAdminCreatorsBody");
    var empty = byId("clikkerAdminCreatorsEmpty");
    var tableCard = byId("clikkerAdminCreatorsTableCard");
    if (!body) return;

    var rows = filteredCreatorRows();
    var pageSize = state.creatorFilters.pageSize;
    var totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    state.creatorFilters.page = Math.min(state.creatorFilters.page, totalPages);
    var start = (state.creatorFilters.page - 1) * pageSize;
    var pageRows = rows.slice(start, start + pageSize);

    setText("clikkerAdminCreatorsResultCount", rows.length);
    setText("clikkerAdminCreatorsPageInfo", "Page " + state.creatorFilters.page + " of " + totalPages);

    if (empty) empty.hidden = rows.length > 0;
    if (tableCard) tableCard.hidden = rows.length === 0;

    body.innerHTML = pageRows.map(function (row) {
      return (
        "<tr>" +
        "<td>" + creatorCell(row) + "</td>" +
        "<td>" + escapeHTML(row.handle ? "@" + row.handle : "—") + "</td>" +
        "<td>" + escapeHTML(row.category || "—") + "</td>" +
        "<td>" + statusHTML(row.accountStatus) + "</td>" +
        "<td>" + statusHTML(row.storeStatus) + "</td>" +
        "<td>" + row.products.length + "</td>" +
        "<td>" + row.orders.length + "</td>" +
        "<td>" + escapeHTML(money(row.netEarnings)) + "</td>" +
        "<td>" + statusHTML(row.payoutStatus) + "</td>" +
        "<td>" + escapeHTML(formatDate(row.joinedAt)) + "</td>" +
        '<td><div class="cad-row-actions">' +
        '<button class="cad-btn cad-btn--secondary cad-btn--small" type="button" data-view-creator="' + escapeHTML(row.id) + '">View</button>' +
        (row.handle ? '<a class="cad-icon-btn" href="' + escapeHTML(ROUTES.publicStore + encodeURIComponent(row.handle)) + '" target="_blank" rel="noopener" aria-label="Open public store">↗</a>' : "") +
        "</div></td>" +
        "</tr>"
      );
    }).join("");

    bindCreatorOpenButtons(body);

    var previous = byId("clikkerAdminCreatorsPreviousPage");
    var next = byId("clikkerAdminCreatorsNextPage");
    if (previous) {
      previous.disabled = state.creatorFilters.page <= 1;
      previous.onclick = function () {
        state.creatorFilters.page -= 1;
        renderCreatorTable();
      };
    }
    if (next) {
      next.disabled = state.creatorFilters.page >= totalPages;
      next.onclick = function () {
        state.creatorFilters.page += 1;
        renderCreatorTable();
      };
    }
  }

  function bindCreatorOpenButtons(scope) {
    (scope || document).querySelectorAll("[data-view-creator]").forEach(function (button) {
      button.addEventListener("click", function () {
        openCreatorDrawer(button.dataset.viewCreator);
      });
    });
  }

  function genericConfig(view) {
    var configs = {
      stores: {
        title: "Stores",
        subtitle: "Review publication, visibility and creator-store health",
        eyebrow: "Store operations",
        description: "Search and inspect all creator stores.",
        columns: ["Store", "Creator", "Username", "Category", "Status", "Visibility", "Published", "Action"]
      },
      products: {
        title: "Products",
        subtitle: "Review products, pricing and publication status",
        eyebrow: "Product operations",
        description: "Search every paid offer created on Clikker.",
        columns: ["Product", "Creator", "Type", "Price", "Status", "Orders", "Created", "Action"]
      },
      orders: {
        title: "Orders",
        subtitle: "Monitor paid orders and creator fulfilment",
        eyebrow: "Commerce operations",
        description: "Review buyer orders, payment value and delivery status.",
        columns: ["Order", "Creator", "Buyer", "Product", "Paid", "Status", "Created", "Action"]
      },
      payouts: {
        title: "Payouts",
        subtitle: "Monitor creator settlements and payout failures",
        eyebrow: "Settlement operations",
        description: "Review payout amount, destination, provider and status.",
        columns: ["Payout", "Creator", "Amount", "Destination", "Status", "Provider", "Processed", "Action"]
      },
      transactions: {
        title: "Transactions",
        subtitle: "Review creator earnings, fees and refunds",
        eyebrow: "Financial ledger",
        description: "Inspect gross value, fees, creator net and ledger status.",
        columns: ["Date", "Reference", "Creator", "Type", "Gross", "Fees", "Net", "Status"]
      }
    };
    return configs[view];
  }

  function genericRows(view) {
    if (view === "stores") {
      return state.creatorRows.filter(function (row) { return !!row.store; }).map(function (row) {
        return {
          search: [row.name, row.handle, row.category, row.storeStatus].join(" "),
          status: row.storeStatus,
          html:
            "<tr>" +
            "<td><strong>" + escapeHTML(firstValue(row.store.storeName, row.store.displayName, row.name)) + "</strong></td>" +
            "<td>" + escapeHTML(row.name) + "</td>" +
            "<td>" + escapeHTML(row.handle ? "@" + row.handle : "—") + "</td>" +
            "<td>" + escapeHTML(row.category) + "</td>" +
            "<td>" + statusHTML(row.storeStatus) + "</td>" +
            "<td>" + statusHTML(row.storeVisibility) + "</td>" +
            "<td>" + escapeHTML(formatDate(firstValue(row.store.publishedAt, row.store.published_at))) + "</td>" +
            '<td><button class="cad-btn cad-btn--secondary cad-btn--small" type="button" data-view-creator="' + escapeHTML(row.id) + '">View</button></td>' +
            "</tr>"
        };
      });
    }

    if (view === "products") {
      return state.products.map(function (product) {
        var creatorRow = state.creatorRows.find(function (row) {
          return belongsToCreator(product, row.creator, row.store);
        });
        var orders = state.orders.filter(function (order) {
          return clean(firstValue(order.productId, order.product_id)) === product.id;
        });
        return {
          search: [product.title, product.type, product.productType, creatorRow && creatorRow.name, product.status].join(" "),
          status: lower(product.status),
          html:
            "<tr>" +
            "<td><strong>" + escapeHTML(product.title || "Untitled product") + "</strong></td>" +
            "<td>" + escapeHTML(creatorRow ? creatorRow.name : "—") + "</td>" +
            "<td>" + escapeHTML(titleCase(firstValue(product.fulfilmentType, product.fulfillmentType, product.productType, product.type))) + "</td>" +
            "<td>" + escapeHTML(money(product.price, product.currency || "INR")) + "</td>" +
            "<td>" + statusHTML(product.status || "draft") + "</td>" +
            "<td>" + orders.length + "</td>" +
            "<td>" + escapeHTML(formatDate(firstValue(product.createdAt, product.created_at))) + "</td>" +
            '<td>' + (creatorRow ? '<button class="cad-btn cad-btn--secondary cad-btn--small" type="button" data-view-creator="' + escapeHTML(creatorRow.id) + '">Creator</button>' : "—") + "</td>" +
            "</tr>"
        };
      });
    }

    if (view === "orders") {
      return state.orders.map(function (order) {
        var creatorRow = state.creatorRows.find(function (row) {
          return belongsToCreator(order, row.creator, row.store);
        });
        return {
          search: [order.id, order.orderName, order.shopifyOrderName, order.buyerName, order.customerName, order.productTitle, creatorRow && creatorRow.name, order.status].join(" "),
          status: lower(order.status),
          html:
            "<tr>" +
            "<td><strong>" + escapeHTML(firstValue(order.orderName, order.shopifyOrderName, order.reference, order.id)) + "</strong></td>" +
            "<td>" + escapeHTML(creatorRow ? creatorRow.name : "—") + "</td>" +
            "<td>" + escapeHTML(firstValue(order.buyerName, order.customerName, order.buyerEmail, order.customerEmail, "—")) + "</td>" +
            "<td>" + escapeHTML(firstValue(order.productTitle, order.product_name, "—")) + "</td>" +
            "<td>" + escapeHTML(money(orderPaidAmount(order), order.currency || "INR")) + "</td>" +
            "<td>" + statusHTML(order.status || "new") + "</td>" +
            "<td>" + escapeHTML(formatDate(firstValue(order.createdAt, order.created_at, order.paidAt, order.paid_at))) + "</td>" +
            '<td>' + (creatorRow ? '<button class="cad-btn cad-btn--secondary cad-btn--small" type="button" data-view-creator="' + escapeHTML(creatorRow.id) + '">Creator</button>' : "—") + "</td>" +
            "</tr>"
        };
      });
    }

    if (view === "payouts") {
      return state.payouts.map(function (payout) {
        var creatorRow = state.creatorRows.find(function (row) {
          return belongsToCreator(payout, row.creator, row.store);
        });
        return {
          search: [payout.id, payout.reference, creatorRow && creatorRow.name, payout.status, payout.provider].join(" "),
          status: lower(payout.status),
          html:
            "<tr>" +
            "<td><strong>" + escapeHTML(firstValue(payout.reference, payout.id)) + "</strong></td>" +
            "<td>" + escapeHTML(creatorRow ? creatorRow.name : "—") + "</td>" +
            "<td>" + escapeHTML(money(payoutAmount(payout), payout.currency || "INR")) + "</td>" +
            "<td>" + escapeHTML(firstValue(payout.destinationLabel, payout.destination_label, payout.accountLast4 ? "••••" + payout.accountLast4 : "—")) + "</td>" +
            "<td>" + statusHTML(payout.status || "pending") + "</td>" +
            "<td>" + escapeHTML(firstValue(payout.provider, "—")) + "</td>" +
            "<td>" + escapeHTML(formatDate(firstValue(payout.processedAt, payout.paidAt, payout.updatedAt, payout.createdAt))) + "</td>" +
            '<td>' + (creatorRow ? '<button class="cad-btn cad-btn--secondary cad-btn--small" type="button" data-view-creator="' + escapeHTML(creatorRow.id) + '">Creator</button>' : "—") + "</td>" +
            "</tr>"
        };
      });
    }

    return state.ledger.map(function (entry) {
      var creatorRow = state.creatorRows.find(function (row) {
        return belongsToCreator(entry, row.creator, row.store);
      });
      return {
        search: [entry.id, entry.reference, entry.type, creatorRow && creatorRow.name, entry.status].join(" "),
        status: lower(entry.status),
        html:
          "<tr>" +
          "<td>" + escapeHTML(formatDate(firstValue(entry.createdAt, entry.created_at, entry.date))) + "</td>" +
          "<td><strong>" + escapeHTML(firstValue(entry.reference, entry.orderName, entry.id)) + "</strong></td>" +
          "<td>" + escapeHTML(creatorRow ? creatorRow.name : "—") + "</td>" +
          "<td>" + escapeHTML(titleCase(firstValue(entry.type, "earning"))) + "</td>" +
          "<td>" + escapeHTML(money(ledgerGross(entry), entry.currency || "INR")) + "</td>" +
          "<td>" + escapeHTML(money(ledgerFees(entry), entry.currency || "INR")) + "</td>" +
          "<td>" + escapeHTML(money(ledgerNet(entry), entry.currency || "INR")) + "</td>" +
          "<td>" + statusHTML(entry.status || "pending") + "</td>" +
          "</tr>"
      };
    });
  }

  function renderGeneric(view) {
    var config = genericConfig(view);
    if (!config) return renderDashboard();
    cloneTemplate(el.clikkerAdminGenericListTemplate);
    setHeader(config.title, config.subtitle, "");

    state.generic.view = view;
    state.generic.rows = genericRows(view);
    state.generic.page = 1;

    setText("clikkerAdminGenericEyebrow", config.eyebrow);
    setText("clikkerAdminGenericHeading", config.title);
    setText("clikkerAdminGenericDescription", config.description);
    setText("clikkerAdminGenericTableTitle", config.title);

    var head = byId("clikkerAdminGenericTableHead");
    if (head) {
      head.innerHTML = "<tr>" + config.columns.map(function (column) {
        return '<th scope="col">' + escapeHTML(column) + "</th>";
      }).join("") + "</tr>";
    }

    var statusOptions = uniqueStatuses(state.generic.rows);
    var filters = byId("clikkerAdminGenericFilters");
    if (filters) {
      filters.innerHTML =
        '<label class="cad-filter"><span>Status</span><select id="clikkerAdminGenericStatusFilter"><option value="all">All statuses</option>' +
        statusOptions.map(function (status) {
          return '<option value="' + escapeHTML(status) + '">' + escapeHTML(titleCase(status)) + "</option>";
        }).join("") +
        "</select></label>";
    }

    bindGenericControls();
    renderGenericTable();
  }

  function uniqueStatuses(rows) {
    var statuses = {};
    rows.forEach(function (row) {
      if (row.status) statuses[row.status] = true;
    });
    return Object.keys(statuses).sort();
  }

  function bindGenericControls() {
    var search = byId("clikkerAdminGenericSearch");
    var status = byId("clikkerAdminGenericStatusFilter");
    var clearButton = byId("clikkerAdminGenericClearFilters");
    var refreshButton = byId("clikkerAdminGenericRefresh");
    var exportButton = byId("clikkerAdminGenericExport");

    if (search) {
      search.addEventListener("input", function () {
        state.generic.search = clean(search.value);
        state.generic.page = 1;
        renderGenericTable();
      });
    }
    if (status) {
      status.addEventListener("change", function () {
        state.generic.status = status.value;
        state.generic.page = 1;
        renderGenericTable();
      });
    }
    if (clearButton) {
      clearButton.addEventListener("click", function () {
        state.generic.search = "";
        state.generic.status = "all";
        if (search) search.value = "";
        if (status) status.value = "all";
        renderGenericTable();
      });
    }
    if (refreshButton) {
      refreshButton.addEventListener("click", async function () {
        refreshButton.disabled = true;
        refreshButton.textContent = "Refreshing…";
        await refreshAllData(false);
        renderGeneric(state.generic.view);
      });
    }
    if (exportButton) {
      exportButton.addEventListener("click", function () {
        exportGenericCSV(state.generic.view);
      });
    }
  }

  function filteredGenericRows() {
    var search = lower(state.generic.search);
    return state.generic.rows.filter(function (row) {
      if (search && lower(row.search).indexOf(search) < 0) return false;
      if (state.generic.status !== "all" && row.status !== state.generic.status) return false;
      return true;
    });
  }

  function renderGenericTable() {
    var body = byId("clikkerAdminGenericTableBody");
    var empty = byId("clikkerAdminGenericEmpty");
    if (!body) return;
    var rows = filteredGenericRows();
    var totalPages = Math.max(1, Math.ceil(rows.length / state.generic.pageSize));
    state.generic.page = Math.min(state.generic.page, totalPages);
    var start = (state.generic.page - 1) * state.generic.pageSize;
    var pageRows = rows.slice(start, start + state.generic.pageSize);

    setText("clikkerAdminGenericResultCount", rows.length);
    setText("clikkerAdminGenericPageInfo", "Page " + state.generic.page + " of " + totalPages);
    if (empty) empty.hidden = rows.length > 0;
    body.innerHTML = pageRows.length
      ? pageRows.map(function (row) { return row.html; }).join("")
      : "";
    bindCreatorOpenButtons(body);

    var previous = byId("clikkerAdminGenericPreviousPage");
    var next = byId("clikkerAdminGenericNextPage");
    if (previous) {
      previous.disabled = state.generic.page <= 1;
      previous.onclick = function () {
        state.generic.page -= 1;
        renderGenericTable();
      };
    }
    if (next) {
      next.disabled = state.generic.page >= totalPages;
      next.onclick = function () {
        state.generic.page += 1;
        renderGenericTable();
      };
    }
  }

  function renderSupport() {
    setHeader("Support", "Review creator support and operational escalations", "");
    el.clikkerAdminView.innerHTML =
      '<section class="cad-hero"><div><span class="cad-eyebrow">Support operations</span><h2>Creator support</h2><p>A dedicated support-ticket collection is not yet connected. This page is ready for future tickets, disputes and escalations.</p></div></section>' +
      '<section class="cad-card"><div class="cad-card__body"><div class="cad-empty"><div class="cad-empty__mark">?</div><h3>No support data source connected</h3><p>Connect a creatorSupportTickets collection or helpdesk backend before enabling ticket actions.</p></div></div></section>';
  }

  function renderSettings() {
    cloneTemplate(el.clikkerAdminSettingsTemplate);
    setHeader("Settings", "Admin account and platform configuration", "");
    setText("clikkerAdminSettingsName", firstValue(state.adminProfile && state.adminProfile.name, state.adminUser && state.adminUser.displayName, "Administrator"));
    setText("clikkerAdminSettingsEmail", firstValue(state.adminProfile && state.adminProfile.email, state.adminUser && state.adminUser.email));
    setText("clikkerAdminSettingsUid", currentUid());
    setText("clikkerAdminSettingsRole", titleCase(state.adminProfile && state.adminProfile.role));
  }

  function creatorBundle(id) {
    return state.creatorRows.find(function (row) { return row.id === id; }) || null;
  }

  function openCreatorDrawer(creatorId) {
    var bundle = creatorBundle(creatorId);
    if (!bundle || !el.clikkerAdminCreatorDrawer) return;
    state.selectedCreatorId = creatorId;
    state.selectedCreatorBundle = bundle;
    state.selectedCreatorTab = "overview";

    el.clikkerAdminCreatorDrawer.hidden = false;
    el.clikkerAdminCreatorDrawer.setAttribute("aria-hidden", "false");
    document.body.classList.add("cad-drawer-open");

    populateCreatorDrawer(bundle);
    switchCreatorTab("overview");
  }

  function closeCreatorDrawer() {
    if (!el.clikkerAdminCreatorDrawer) return;
    el.clikkerAdminCreatorDrawer.hidden = true;
    el.clikkerAdminCreatorDrawer.setAttribute("aria-hidden", "true");
    document.body.classList.remove("cad-drawer-open");
    state.selectedCreatorId = "";
    state.selectedCreatorBundle = null;
  }

  function switchCreatorTab(tab) {
    state.selectedCreatorTab = tab;
    if (!el.clikkerAdminCreatorDrawer) return;
    el.clikkerAdminCreatorDrawer.querySelectorAll("[data-creator-tab]").forEach(function (button) {
      var active = button.dataset.creatorTab === tab;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-selected", active ? "true" : "false");
    });
    el.clikkerAdminCreatorDrawer.querySelectorAll("[data-creator-panel]").forEach(function (panel) {
      panel.classList.toggle("is-active", panel.dataset.creatorPanel === tab);
    });
  }

  function populateCreatorDrawer(bundle) {
    var creator = bundle.creator;
    var user = bundle.user || {};
    var store = bundle.store || {};
    var payoutProfile = payoutProfileOf(creator);
    var payoutSummary = payoutSummaryOf(creator);
    var initial = (bundle.name || "C").charAt(0).toUpperCase();
    var photo = clean(firstValue(creator.profileImageURL, creator.profileImage, user.photoURL, store.profileImageURL));

    if (el.clikkerAdminCreatorDrawerAvatar) {
      el.clikkerAdminCreatorDrawerAvatar.innerHTML = photo
        ? '<img src="' + escapeHTML(photo) + '" alt="">'
        : escapeHTML(initial);
    }
    setText("clikkerAdminCreatorDrawerTitle", bundle.name);
    setText("clikkerAdminCreatorDrawerMeta", bundle.email || "Creator account");
    setText("clikkerAdminCreatorDrawerCreatorId", "Creator: " + bundle.id);
    setText("clikkerAdminCreatorDrawerStoreId", "Store: " + (store.id || "—"));
    if (el.clikkerAdminCreatorDrawerAccountStatus) {
      el.clikkerAdminCreatorDrawerAccountStatus.outerHTML = statusHTML(bundle.accountStatus).replace("<span", '<span id="clikkerAdminCreatorDrawerAccountStatus"');
      el.clikkerAdminCreatorDrawerAccountStatus = byId("clikkerAdminCreatorDrawerAccountStatus");
    }

    setText("clikkerAdminCreatorProductsCount", bundle.products.length);
    setText("clikkerAdminCreatorOrdersCount", bundle.orders.length);
    setText("clikkerAdminCreatorGrossSales", money(bundle.grossSales));
    setText("clikkerAdminCreatorNetEarnings", money(bundle.netEarnings));
    setText("clikkerAdminCreatorPendingPayout", money(firstValue(payoutSummary.pendingBalance, payoutSummary.pending_balance, bundle.pendingBalance)));
    setText("clikkerAdminCreatorTotalPaid", money(firstValue(payoutSummary.totalPaid, payoutSummary.total_paid, bundle.totalPaid)));

    setText("clikkerAdminCreatorName", bundle.name);
    setText("clikkerAdminCreatorEmail", bundle.email || "—");
    setText("clikkerAdminCreatorPhone", bundle.phone || "—");
    setText("clikkerAdminCreatorHandle", bundle.handle ? "@" + bundle.handle : "—");
    setText("clikkerAdminCreatorCategory", bundle.category || "—");
    setText("clikkerAdminCreatorJoinedAt", formatDate(bundle.joinedAt, true));
    setText("clikkerAdminCreatorOnboardingStatus", bundle.onboardingCompleted ? "Completed" : "Incomplete");
    setText("clikkerAdminCreatorLastActive", formatDate(bundle.lastActiveAt, true));

    setValue("clikkerAdminCreatorAccountStatus", bundle.accountStatus);
    setValue("clikkerAdminCreatorStoreStatus", bundle.storeStatus === "missing" ? "draft" : bundle.storeStatus);
    setValue("clikkerAdminCreatorStoreVisibility", bundle.storeVisibility);
    setValue("clikkerAdminCreatorStatusNote", "");

    populateStorePanel(bundle);
    populateProductsPanel(bundle);
    populateOrdersPanel(bundle);
    populatePayoutPanel(bundle, payoutProfile);
    populateTransactionsPanel(bundle);
    populateDocumentsPanel(bundle);
    populateActivityPanel(bundle);

    var profileLink = byId("clikkerAdminCreatorOpenProfile");
    if (profileLink) {
      profileLink.href = ROUTES.creator + "?admin_creator=" + encodeURIComponent(bundle.id);
    }

    bindCreatorDrawerActions();
  }

  function populateStorePanel(bundle) {
    var store = bundle.store || {};
    var contact = store.contact || {};
    var name = clean(firstValue(store.storeName, store.displayName, bundle.name));
    var tagline = clean(firstValue(store.tagline, store.shortBio, store.about, "—"));
    var avatar = clean(firstValue(store.profileImageURL, store.profileImage, bundle.creator.profileImageURL));
    var banner = clean(firstValue(store.bannerImageURL, store.bannerImage, store.coverImageURL));
    var url = bundle.handle ? location.origin + ROUTES.publicStore + encodeURIComponent(bundle.handle) : "";

    var bannerNode = byId("clikkerAdminCreatorStoreBanner");
    if (bannerNode) {
      bannerNode.style.backgroundImage = banner ? 'url("' + banner.replace(/"/g, "") + '")' : "";
      bannerNode.classList.toggle("has-image", !!banner);
    }
    var avatarNode = byId("clikkerAdminCreatorStoreAvatar");
    if (avatarNode) {
      avatarNode.innerHTML = avatar ? '<img src="' + escapeHTML(avatar) + '" alt="">' : escapeHTML(name.charAt(0).toUpperCase());
    }
    setText("clikkerAdminCreatorStoreName", name);
    setText("clikkerAdminCreatorStoreTagline", tagline);
    setText("clikkerAdminCreatorStoreUrl", url || "—");
    setText("clikkerAdminCreatorStoreStatusText", titleCase(bundle.storeStatus));
    setText("clikkerAdminCreatorStoreVisibilityText", titleCase(bundle.storeVisibility));
    setText("clikkerAdminCreatorStorePublishedAt", formatDate(firstValue(store.publishedAt, store.published_at), true));
    setText("clikkerAdminCreatorStoreSupportEmail", firstValue(contact.supportEmail, store.supportEmail, bundle.email, "—"));
    setText("clikkerAdminCreatorStoreProfileStrength", firstValue(store.profileStrength, store.profile_strength, store.profileScore, "—"));

    var openStore = byId("clikkerAdminCreatorOpenStore");
    if (openStore) {
      openStore.href = bundle.handle ? ROUTES.publicStore + encodeURIComponent(bundle.handle) : "#";
      openStore.setAttribute("aria-disabled", bundle.handle ? "false" : "true");
    }
  }

  function populateProductsPanel(bundle) {
    var body = byId("clikkerAdminCreatorProductsBody");
    if (!body) return;
    body.innerHTML = bundle.products.length
      ? bundle.products.map(function (product) {
          var orders = bundle.orders.filter(function (order) {
            return clean(firstValue(order.productId, order.product_id)) === product.id;
          });
          var revenue = orders.reduce(function (sum, order) { return sum + orderPaidAmount(order); }, 0);
          return (
            "<tr>" +
            "<td><strong>" + escapeHTML(product.title || "Untitled product") + "</strong></td>" +
            "<td>" + escapeHTML(titleCase(firstValue(product.fulfilmentType, product.fulfillmentType, product.productType, product.type))) + "</td>" +
            "<td>" + escapeHTML(money(product.price, product.currency || "INR")) + "</td>" +
            "<td>" + statusHTML(product.status || "draft") + "</td>" +
            "<td>" + orders.length + "</td>" +
            "<td>" + escapeHTML(money(revenue, product.currency || "INR")) + "</td>" +
            "<td>" + escapeHTML(formatDate(firstValue(product.createdAt, product.created_at))) + "</td>" +
            "</tr>"
          );
        }).join("")
      : '<tr><td colspan="7" class="cad-table__empty">No products found.</td></tr>';
  }

  function populateOrdersPanel(bundle) {
    var body = byId("clikkerAdminCreatorOrdersBody");
    if (!body) return;
    body.innerHTML = bundle.orders.length
      ? bundle.orders
          .slice()
          .sort(function (a, b) {
            return timestampMs(firstValue(b.createdAt, b.paidAt)) - timestampMs(firstValue(a.createdAt, a.paidAt));
          })
          .map(function (order) {
            return (
              "<tr>" +
              "<td><strong>" + escapeHTML(firstValue(order.orderName, order.shopifyOrderName, order.reference, order.id)) + "</strong></td>" +
              "<td>" + escapeHTML(firstValue(order.buyerName, order.customerName, order.buyerEmail, order.customerEmail, "—")) + "</td>" +
              "<td>" + escapeHTML(firstValue(order.productTitle, order.product_name, "—")) + "</td>" +
              "<td>" + escapeHTML(money(orderPaidAmount(order), order.currency || "INR")) + "</td>" +
              "<td>" + statusHTML(order.status || "new") + "</td>" +
              "<td>" + escapeHTML(formatDate(firstValue(order.deliveryDeadline, order.deadline, order.dueAt))) + "</td>" +
              "<td>" + escapeHTML(formatDate(firstValue(order.createdAt, order.paidAt))) + "</td>" +
              "</tr>"
            );
          }).join("")
      : '<tr><td colspan="7" class="cad-table__empty">No orders found.</td></tr>';
  }

  function populatePayoutPanel(bundle, profile) {
    setText("clikkerAdminCreatorPayoutMethod", titleCase(firstValue(profile.method, "—")));
    setText("clikkerAdminCreatorPayoutHolder", firstValue(profile.accountHolderName, profile.account_holder_name, "—"));
    setText("clikkerAdminCreatorPayoutBank", firstValue(profile.bankName, profile.bank_name, "—"));
    setText("clikkerAdminCreatorPayoutAccountMasked", firstValue(profile.accountMasked, profile.account_masked, profile.accountLast4 ? "••••" + profile.accountLast4 : "—"));
    setText("clikkerAdminCreatorPayoutIfsc", firstValue(profile.ifsc, "—"));
    setText("clikkerAdminCreatorPayoutUpi", firstValue(profile.upiMasked, profile.upi_masked, "—"));
    setText("clikkerAdminCreatorPayoutProvider", firstValue(profile.provider, "—"));
    setText("clikkerAdminCreatorPayoutUpdatedAt", formatDate(firstValue(profile.updatedAt, profile.updated_at), true));
    setValue("clikkerAdminCreatorPayoutVerificationStatus", bundle.payoutStatus);
    setValue("clikkerAdminCreatorPayoutVerificationNote", firstValue(profile.adminNote, profile.admin_note, ""));

    var statusNode = byId("clikkerAdminCreatorPayoutAccountStatus");
    if (statusNode) {
      statusNode.outerHTML = statusHTML(bundle.payoutStatus).replace("<span", '<span id="clikkerAdminCreatorPayoutAccountStatus"');
    }

    var body = byId("clikkerAdminCreatorPayoutsBody");
    if (body) {
      body.innerHTML = bundle.payouts.length
        ? bundle.payouts
            .slice()
            .sort(function (a, b) {
              return timestampMs(firstValue(b.processedAt, b.paidAt, b.createdAt)) - timestampMs(firstValue(a.processedAt, a.paidAt, a.createdAt));
            })
            .map(function (payout) {
              return (
                "<tr>" +
                "<td><strong>" + escapeHTML(firstValue(payout.reference, payout.id)) + "</strong></td>" +
                "<td>" + escapeHTML(money(payoutAmount(payout), payout.currency || "INR")) + "</td>" +
                "<td>" + escapeHTML(firstValue(payout.destinationLabel, payout.destination_label, payout.accountLast4 ? "••••" + payout.accountLast4 : "—")) + "</td>" +
                "<td>" + statusHTML(payout.status || "pending") + "</td>" +
                "<td>" + escapeHTML(firstValue(payout.providerPayoutId, payout.provider_payout_id, payout.utr, "—")) + "</td>" +
                "<td>" + escapeHTML(formatDate(firstValue(payout.processedAt, payout.paidAt, payout.updatedAt), true)) + "</td>" +
                "</tr>"
              );
            }).join("")
        : '<tr><td colspan="6" class="cad-table__empty">No payouts found.</td></tr>';
    }
  }

  function populateTransactionsPanel(bundle) {
    var body = byId("clikkerAdminCreatorTransactionsBody");
    if (!body) return;
    body.innerHTML = bundle.ledger.length
      ? bundle.ledger
          .slice()
          .sort(function (a, b) {
            return timestampMs(firstValue(b.createdAt, b.date)) - timestampMs(firstValue(a.createdAt, a.date));
          })
          .map(function (entry) {
            return (
              "<tr>" +
              "<td>" + escapeHTML(formatDate(firstValue(entry.createdAt, entry.date))) + "</td>" +
              "<td><strong>" + escapeHTML(firstValue(entry.reference, entry.orderName, entry.id)) + "</strong></td>" +
              "<td>" + escapeHTML(titleCase(firstValue(entry.type, "earning"))) + "</td>" +
              "<td>" + escapeHTML(money(ledgerGross(entry), entry.currency || "INR")) + "</td>" +
              "<td>" + escapeHTML(money(ledgerFees(entry), entry.currency || "INR")) + "</td>" +
              "<td>" + escapeHTML(money(ledgerNet(entry), entry.currency || "INR")) + "</td>" +
              "<td>" + statusHTML(entry.status || "pending") + "</td>" +
              "</tr>"
            );
          }).join("")
      : '<tr><td colspan="7" class="cad-table__empty">No ledger entries found.</td></tr>';
  }

  function populateDocumentsPanel(bundle) {
    var list = byId("clikkerAdminCreatorDocumentsList");
    var empty = byId("clikkerAdminCreatorDocumentsEmpty");
    if (!list || !empty) return;
    list.innerHTML = bundle.documents.map(function (doc) {
      var url = clean(firstValue(doc.downloadURL, doc.url, doc.fileURL));
      return (
        '<article class="cad-document-item">' +
        '<span class="cad-document-item__icon">▤</span>' +
        "<div><strong>" + escapeHTML(firstValue(doc.title, doc.documentType, doc.type, doc.fileName, "Document")) + "</strong>" +
        "<small>" + escapeHTML(titleCase(firstValue(doc.status, "uploaded"))) + " · " + escapeHTML(formatDate(firstValue(doc.updatedAt, doc.createdAt))) + "</small></div>" +
        (url ? '<a class="cad-btn cad-btn--secondary cad-btn--small" href="' + escapeHTML(url) + '" target="_blank" rel="noopener">Open</a>' : "") +
        "</article>"
      );
    }).join("");
    empty.hidden = bundle.documents.length > 0;
    list.hidden = bundle.documents.length === 0;
  }

  function populateActivityPanel(bundle) {
    var list = byId("clikkerAdminCreatorActivityList");
    var empty = byId("clikkerAdminCreatorActivityEmpty");
    if (!list || !empty) return;
    var activity = bundle.auditLogs.slice().sort(function (a, b) {
      return timestampMs(firstValue(b.createdAt, b.timestamp)) - timestampMs(firstValue(a.createdAt, a.timestamp));
    });
    list.innerHTML = activity.map(function (log) {
      return (
        '<li class="cad-activity-item">' +
        '<span class="cad-activity-item__mark">•</span>' +
        "<div><strong>" + escapeHTML(titleCase(firstValue(log.action, log.type, "activity"))) + "</strong>" +
        "<p>" + escapeHTML(firstValue(log.note, log.reason, log.description, "Admin activity")) + "</p>" +
        "<small>" + escapeHTML(formatDate(firstValue(log.createdAt, log.timestamp), true)) + "</small></div>" +
        "</li>"
      );
    }).join("");
    empty.hidden = activity.length > 0;
    list.hidden = activity.length === 0;
  }

  function bindCreatorDrawerActions() {
    if (el.clikkerAdminCreatorDrawerTabs) {
      el.clikkerAdminCreatorDrawerTabs.querySelectorAll("[data-creator-tab]").forEach(function (button) {
        button.onclick = function () {
          switchCreatorTab(button.dataset.creatorTab);
        };
      });
    }

    var saveStatus = byId("clikkerAdminCreatorSaveStatus");
    if (saveStatus) {
      saveStatus.onclick = function () {
        var account = byId("clikkerAdminCreatorAccountStatus").value;
        var storeStatus = byId("clikkerAdminCreatorStoreStatus").value;
        var visibility = byId("clikkerAdminCreatorStoreVisibility").value;
        var note = clean(byId("clikkerAdminCreatorStatusNote").value);
        var dangerous = ["suspended", "disabled"].indexOf(account) >= 0 || storeStatus === "suspended";
        if (dangerous && !note) {
          localAlert("clikkerAdminCreatorDrawerAlert", "Add an admin note before suspending or disabling access.", "error");
          return;
        }
        openConfirm({
          title: "Save creator status changes?",
          message: "This will update the creator account and store operational status.",
          confirmText: dangerous ? "Confirm restriction" : "Save changes",
          danger: dangerous,
          reasonRequired: dangerous,
          defaultReason: note,
          onConfirm: function (reason) {
            return saveCreatorStatus(account, storeStatus, visibility, reason || note);
          }
        });
      };
    }

    var savePayout = byId("clikkerAdminCreatorSavePayoutVerification");
    if (savePayout) {
      savePayout.onclick = function () {
        var status = byId("clikkerAdminCreatorPayoutVerificationStatus").value;
        var note = clean(byId("clikkerAdminCreatorPayoutVerificationNote").value);
        if (["rejected", "needs_update"].indexOf(status) >= 0 && !note) {
          localAlert("clikkerAdminCreatorDrawerAlert", "Add a verification note for rejected or update-required statuses.", "error");
          return;
        }
        openConfirm({
          title: "Update payout verification?",
          message: "This changes the admin review status only. It does not execute or approve a real payout.",
          confirmText: "Update verification",
          danger: status === "rejected",
          reasonRequired: status === "rejected",
          defaultReason: note,
          onConfirm: function (reason) {
            return savePayoutVerification(status, reason || note);
          }
        });
      };
    }
  }

  async function saveCreatorStatus(accountStatus, storeStatus, visibility, note) {
    var bundle = state.selectedCreatorBundle;
    if (!bundle) return;
    clearLocalAlert("clikkerAdminCreatorDrawerAlert");
    var batch = db().batch();
    var creatorRef = db().collection("creators").doc(bundle.creator.id);
    batch.set(
      creatorRef,
      {
        status: accountStatus,
        adminStatusNote: note || "",
        admin_status_note: note || "",
        updatedAt: serverTimestamp(),
        updated_at: serverTimestamp()
      },
      { merge: true }
    );

    if (bundle.user && bundle.user.id) {
      batch.set(
        db().collection("users").doc(bundle.user.id),
        {
          status: accountStatus,
          updatedAt: serverTimestamp(),
          updated_at: serverTimestamp()
        },
        { merge: true }
      );
    }

    if (bundle.store && bundle.store.id) {
      batch.set(
        db().collection("stores").doc(bundle.store.id),
        {
          status: storeStatus,
          visibility: visibility,
          adminStatusNote: note || "",
          admin_status_note: note || "",
          updatedAt: serverTimestamp(),
          updated_at: serverTimestamp()
        },
        { merge: true }
      );
    }

    var auditRef = db().collection("adminAuditLogs").doc();
    batch.set(auditRef, {
      action: "creator_status_updated",
      adminId: currentUid(),
      adminEmail: clean(state.adminUser && state.adminUser.email),
      creatorId: bundle.id,
      ownerId: ownerIdOf(bundle.creator),
      storeId: bundle.store && bundle.store.id || "",
      before: {
        accountStatus: bundle.accountStatus,
        storeStatus: bundle.storeStatus,
        visibility: bundle.storeVisibility
      },
      after: {
        accountStatus: accountStatus,
        storeStatus: storeStatus,
        visibility: visibility
      },
      note: note || "",
      createdAt: serverTimestamp()
    });

    await batch.commit();
    await refreshAllData(true);
    state.selectedCreatorBundle = creatorBundle(bundle.id);
    populateCreatorDrawer(state.selectedCreatorBundle);
    localAlert("clikkerAdminCreatorDrawerAlert", "Creator and store status updated.", "success");
    if (state.currentView === "creators") renderCreatorTable();
  }

  async function savePayoutVerification(status, note) {
    var bundle = state.selectedCreatorBundle;
    if (!bundle) return;
    clearLocalAlert("clikkerAdminCreatorDrawerAlert");
    var creatorRef = db().collection("creators").doc(bundle.creator.id);
    var auditRef = db().collection("adminAuditLogs").doc();
    var batch = db().batch();

    batch.set(
      creatorRef,
      {
        payoutProfile: {
          status: status,
          adminNote: note || "",
          reviewedBy: currentUid(),
          reviewedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        },
        payout_profile: {
          status: status,
          admin_note: note || "",
          reviewed_by: currentUid(),
          reviewed_at: serverTimestamp(),
          updated_at: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );

    batch.set(auditRef, {
      action: "payout_verification_updated",
      adminId: currentUid(),
      adminEmail: clean(state.adminUser && state.adminUser.email),
      creatorId: bundle.id,
      ownerId: ownerIdOf(bundle.creator),
      storeId: bundle.store && bundle.store.id || "",
      before: { status: bundle.payoutStatus },
      after: { status: status },
      note: note || "",
      createdAt: serverTimestamp()
    });

    await batch.commit();
    await refreshAllData(true);
    state.selectedCreatorBundle = creatorBundle(bundle.id);
    populateCreatorDrawer(state.selectedCreatorBundle);
    switchCreatorTab("payouts");
    localAlert("clikkerAdminCreatorDrawerAlert", "Payout verification status updated. No money was transferred.", "success");
  }

  function openConfirm(options) {
    state.pendingConfirm = options;
    if (!el.clikkerAdminConfirmDialog) return;
    el.clikkerAdminConfirmDialog.hidden = false;
    el.clikkerAdminConfirmDialog.setAttribute("aria-hidden", "false");
    setText("clikkerAdminConfirmTitle", options.title || "Confirm action");
    setText("clikkerAdminConfirmMessage", options.message || "Review this action before continuing.");
    if (el.clikkerAdminConfirmIcon) el.clikkerAdminConfirmIcon.textContent = options.danger ? "!" : "✓";
    if (el.clikkerAdminConfirmReasonWrap) el.clikkerAdminConfirmReasonWrap.hidden = options.reasonHidden === true;
    if (el.clikkerAdminConfirmReason) el.clikkerAdminConfirmReason.value = options.defaultReason || "";
    if (el.clikkerAdminConfirmSubmit) {
      el.clikkerAdminConfirmSubmit.textContent = options.confirmText || "Confirm";
      el.clikkerAdminConfirmSubmit.className = options.danger
        ? "cad-btn cad-btn--danger"
        : "cad-btn cad-btn--primary";
    }
    clearLocalAlert("clikkerAdminConfirmAlert");
  }

  function closeConfirm() {
    if (!el.clikkerAdminConfirmDialog) return;
    el.clikkerAdminConfirmDialog.hidden = true;
    el.clikkerAdminConfirmDialog.setAttribute("aria-hidden", "true");
    state.pendingConfirm = null;
    if (el.clikkerAdminConfirmReason) el.clikkerAdminConfirmReason.value = "";
    clearLocalAlert("clikkerAdminConfirmAlert");
  }

  async function submitConfirm() {
    var options = state.pendingConfirm;
    if (!options || !options.onConfirm) return;
    var reason = clean(el.clikkerAdminConfirmReason && el.clikkerAdminConfirmReason.value);
    if (options.reasonRequired && !reason) {
      localAlert("clikkerAdminConfirmAlert", "Add a reason before continuing.", "error");
      return;
    }
    try {
      el.clikkerAdminConfirmSubmit.disabled = true;
      el.clikkerAdminConfirmSubmit.textContent = "Saving…";
      await options.onConfirm(reason);
      closeConfirm();
    } catch (error) {
      console.error("[Clikker Admin] Confirmed action failed", error);
      localAlert("clikkerAdminConfirmAlert", firebaseMessage(error, "The action could not be completed."), "error");
    } finally {
      if (el.clikkerAdminConfirmSubmit) {
        el.clikkerAdminConfirmSubmit.disabled = false;
        el.clikkerAdminConfirmSubmit.textContent = options.confirmText || "Confirm";
      }
    }
  }

  function exportCreatorsCSV(rows) {
    var output = rows.map(function (row) {
      return {
        Creator_ID: row.id,
        User_ID: ownerIdOf(row.creator),
        Store_ID: row.store && row.store.id || "",
        Name: row.name,
        Email: row.email,
        Username: row.handle,
        Category: row.category,
        Account_Status: row.accountStatus,
        Onboarding_Completed: row.onboardingCompleted,
        Store_Status: row.storeStatus,
        Products: row.products.length,
        Orders: row.orders.length,
        Gross_Sales: row.grossSales,
        Net_Earnings: row.netEarnings,
        Payout_Status: row.payoutStatus,
        Joined_At: formatDate(row.joinedAt, true)
      };
    });
    downloadCSV("clikker-creators.csv", output);
  }

  function exportGenericCSV(view) {
    var rows = [];
    if (view === "stores") rows = state.stores;
    else if (view === "products") rows = state.products;
    else if (view === "orders") rows = state.orders;
    else if (view === "payouts") rows = state.payouts;
    else rows = state.ledger;
    downloadCSV("clikker-" + view + ".csv", rows);
  }

  function csvCell(value) {
    if (value && typeof value === "object") {
      if (value.toDate && typeof value.toDate === "function") value = value.toDate().toISOString();
      else value = JSON.stringify(value);
    }
    var string = String(value == null ? "" : value).replace(/"/g, '""');
    return '"' + string + '"';
  }

  function downloadCSV(filename, rows) {
    if (!rows || !rows.length) {
      showAlert("There is no data to export.", "info");
      return;
    }
    var columns = {};
    rows.forEach(function (row) {
      Object.keys(row || {}).forEach(function (key) { columns[key] = true; });
    });
    var headers = Object.keys(columns);
    var csv = [headers.map(csvCell).join(",")]
      .concat(rows.map(function (row) {
        return headers.map(function (header) { return csvCell(row && row[header]); }).join(",");
      }))
      .join("\r\n");
    var blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    var url = URL.createObjectURL(blob);
    var link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
  }

  async function logout() {
    try {
      await auth().signOut();
    } finally {
      location.href = ROUTES.login;
    }
  }

  function bindStaticEvents() {
    if (el.clikkerAdminNavigation) {
      el.clikkerAdminNavigation.addEventListener("click", function (event) {
        var button = event.target.closest("[data-admin-view]");
        if (button) renderView(button.dataset.adminView);
      });
    }

    if (el.clikkerAdminMobileMenu) {
      el.clikkerAdminMobileMenu.addEventListener("click", function () {
        el.clikkerAdminSidebar.classList.toggle("is-open");
      });
    }

    [el.clikkerAdminLogout, el.clikkerAdminDeniedLogout].forEach(function (button) {
      if (button) button.addEventListener("click", logout);
    });

    if (el.clikkerAdminRetry) el.clikkerAdminRetry.addEventListener("click", boot);

    document.addEventListener("click", function (event) {
      if (event.target.closest("[data-close-creator-drawer]")) closeCreatorDrawer();
      if (event.target.closest("[data-close-admin-confirm]")) closeConfirm();
    });

    if (el.clikkerAdminConfirmSubmit) el.clikkerAdminConfirmSubmit.addEventListener("click", submitConfirm);

    document.addEventListener("keydown", function (event) {
      if (event.key !== "Escape") return;
      if (el.clikkerAdminConfirmDialog && !el.clikkerAdminConfirmDialog.hidden) closeConfirm();
      else if (el.clikkerAdminCreatorDrawer && !el.clikkerAdminCreatorDrawer.hidden) closeCreatorDrawer();
      else if (el.clikkerAdminSidebar) el.clikkerAdminSidebar.classList.remove("is-open");
    });
  }

  function init() {
    cache();
    bindStaticEvents();
    boot();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
